import React, { useState, useContext, useEffect } from 'react';
import propTypes from 'prop-types';
import { QuantityContext } from '../QuantitySelector/QuantityProvider';
import { QuoteContext } from './QuoteProvider';
import { StateFees } from './StateFees';
import {
  generateProductsList, generateSubtotals, totalPrice, isFreeAlignmentEligible,
} from './Checkout/helpers';
import { formatCurrency } from '../../utilities/financing';
import { productPropTypes } from './productPropTypes';

const QuoteTotal = ({
  product, product2, storeDetails, isSplitFitment, isTrailerTire,
}) => {
  const { installOption, addons } = useContext(QuoteContext);
  const { quantities } = useContext(QuantityContext);
  const { pricingGroup, state } = window.drupalSettings.storeDetails;
  const [subtotals, setSubtotals] = useState({});
  const [total, setTotal] = useState(0);
  const [rebateValue, setRebateValue] = useState(0);
  const [isAlignmentFree, setIsAlignmentFree] = useState(
    isFreeAlignmentEligible({
      isTrailerTire, installOption, quantities, isSplitFitment, product,
    }),
  );

  useEffect(() => {
    if (!installOption.subs) {
      return;
    }

    let qty = quantities.qtyFront;
    if (product2 != null) {
      qty += quantities.qtyRear;
    }

    const products = generateProductsList(installOption, addons, qty);

    const subtotalObj = generateSubtotals({
      frontTire: product,
      rearTire: product2,
      pricingGroup,
      products,
      state,
      quantities,
    });

    const totalSum = totalPrice(subtotalObj);
    const freeAlignment = isFreeAlignmentEligible({
      isTrailerTire, installOption, quantities, isSplitFitment, product,
    });

    setSubtotals(subtotalObj);
    setTotal(totalSum);
    setRebateValue(subtotalObj.rebates);
    setIsAlignmentFree(freeAlignment);
  }, [
    addons,
    installOption,
    pricingGroup,
    product,
    product2,
    quantities,
    state,
    isTrailerTire,
    isSplitFitment,
  ]);

  return (
    <table className="cmp-quote-total">
      <tbody>
        <tr>
          <th scope="row">
            Subtotal
          </th>
          <td>{formatCurrency(subtotals.subtotal)}</td>
        </tr>
        {subtotals.savings < 0 && (
          <tr>
            <th scope="row">
              Instant Savings
            </th>
            <td>
              -
              {formatCurrency(subtotals.savings)}
            </td>
          </tr>
        )}
        {isAlignmentFree && (
          <tr>
            <th scope="row">
              {/* Alignment (Free!) */}
              Free Alignment and Full Synthetic Oil Change
            </th>
            <td>
              <s className="cmp-quote-total__strike">
                {/* $129.99 */}
                $229.98
              </s>
            </td>
          </tr>
        )}
        <StateFees
          isSplitFitment={isSplitFitment}
          statePostal={storeDetails?.state}
        />
        <tr>
          <th scope="row">
            Subject to Local Tax Rates
          </th>
          <td>
            + Tax
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <th scope="row" className="cmp-quote-total__total-label">
            Estimated Total
          </th>
          <td className="cmp-quote-total__total-price">
            {formatCurrency(total)}
            *
            <br />
            <span className="cmp-quote-total__plus-tax">
              plus tax
            </span>
          </td>
        </tr>
        {rebateValue > 0 && (
          <tr>
            <th scope="row" className="cmp-quote-total__rebates-label">
              Price after mail-in rebates
            </th>
            <td className="cmp-quote-total__rebates-price">
              {formatCurrency(total - rebateValue)}
              <br />
              <span className="cmp-quote-total__plus-tax">
                plus tax
              </span>
            </td>
          </tr>
        )}
      </tfoot>
    </table>
  );
};

QuoteTotal.propTypes = {
  product: productPropTypes,
  product2: productPropTypes,
  storeDetails: propTypes.shape({
    number: propTypes.string,
    name: propTypes.string,
    phone: propTypes.string,
    phoneVisual: propTypes.string,
    phoneAccessible: propTypes.string,
    storeLink: propTypes.string,
    distanceMiles: propTypes.number,
    pricingGroup: propTypes.string,
    yelpUrl: propTypes.string,
    facebookUrl: propTypes.string,
    state: propTypes.string,
  }),
  isSplitFitment: propTypes.bool,
  isTrailerTire: propTypes.bool,
};

export { QuoteTotal };
